import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type DealDocumentsResult } from '@whinerp/client';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The deal's documents in one place (G-089): its own files and its quotes',
 * grouped by kind, newest version of each.
 *
 * Uploading stays in the Files panel - a document is a file, and a second
 * upload box would be a second place for the same file to be. Files not yet
 * sorted are shown as such, first, because they are the ones to look at.
 */

const KINDS = ['proposal', 'drawing', 'specification', 'contract', 'other'] as const;
type Kind = (typeof KINDS)[number];

export function DealDocumentsPanel({ dealId }: { dealId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-032').enabled;
  const key = ['deal-documents', dealId];

  const documents = useQuery<DealDocumentsResult, ApiError>({
    queryKey: key,
    queryFn: () => api.dealDocuments({ path: { dealId } }),
    enabled,
  });

  const file = useMutation({
    mutationFn: (input: { attachmentId: string; kind: Kind | null }) =>
      api.setDealDocumentKind({ path: { dealId, attachmentId: input.attachmentId }, body: { kind: input.kind } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  if (!enabled || documents.isPending) return null;
  if (documents.isError) {
    return (
      <Panel data-region="deal-documents">
        <p className="text-dense text-danger-text">{errorText(documents.error)}</p>
      </Panel>
    );
  }
  const all = documents.data.documents;
  if (all.length === 0) return null;

  const groups: Array<{ kind: Kind | null; entries: typeof all }> = [
    { kind: null, entries: all.filter((entry) => entry.kind === null) },
    ...KINDS.map((kind) => ({ kind, entries: all.filter((entry) => entry.kind === kind) })),
  ].filter((group) => group.entries.length > 0);

  return (
    <Panel data-region="deal-documents">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('dealDocuments.title')}</SectionLabel>
        {groups.map((group) => (
          <section key={group.kind ?? 'unsorted'} data-document-group={group.kind ?? 'unsorted'} className="flex flex-col gap-1">
            <h3 className="text-meta text-ink-meta">
              {group.kind ? t(`dealDocuments.kind.${group.kind}`) : t('dealDocuments.unsorted')}
            </h3>
            <ul className="flex flex-col gap-1">
              {group.entries.map((entry) => (
                <li key={entry.attachmentId} data-document={entry.attachmentId}
                  className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2 text-dense">
                  <span className="text-ink">{entry.filename}</span>
                  {entry.version > 1 ? (
                    <span className="text-meta text-ink-meta">{t('dealDocuments.version', { values: { n: String(entry.version) } })}</span>
                  ) : null}
                  {entry.source === 'quote' ? (
                    <span className="text-meta text-ink-meta" data-document-quote>
                      {t('dealDocuments.onQuote', { values: { number: entry.quoteNumber ?? '' } })}
                    </span>
                  ) : null}
                  {entry.generated ? <span className="text-meta text-ink-meta">{t('dealDocuments.generated')}</span> : null}
                  <select
                    aria-label={t('dealDocuments.kind')}
                    className="ms-auto h-input rounded-control border border-control bg-surface px-2 text-meta text-ink"
                    data-document-kind={entry.attachmentId}
                    value={entry.kind ?? ''}
                    disabled={file.isPending}
                    onChange={(event) =>
                      file.mutate({ attachmentId: entry.attachmentId, kind: (event.target.value || null) as Kind | null })
                    }
                  >
                    <option value="">{t('dealDocuments.unsorted')}</option>
                    {KINDS.map((kind) => (
                      <option key={kind} value={kind}>{t(`dealDocuments.kind.${kind}`)}</option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {file.error ? <p className="text-dense text-danger-text">{errorText(file.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}
